import { useEffect, useRef, useState } from 'react';
import { act } from '../lib/actions';
import { api, upload } from '../lib/api';
import { useNova } from '../lib/store';
import type { ChatMessage, ChatView } from '../lib/types';
import { CannedPicker, fill, useCanned } from './Canned';
import { CHANNELS } from './ContextPanel';
import { FileList } from './Files';
import { Button, Elapsed, Icon } from './ui';

/**
 * One livechat in the middle of the Work screen: offered (Accept or
 * Decline), live (the thread and a reply box), then wrap-up with an outcome,
 * like a call.
 */
export function ChatPane({ chat }: { chat: ChatView }) {
  const id = chat.interactionId;
  const messages = useNova((s) => s.chatMessages[id]);
  const typing = useNova((s) => (s.chatTyping[id] ?? 0) > Date.now());
  const thread = useRef<HTMLDivElement>(null);

  // The thread so far; new messages then arrive as events
  useEffect(() => {
    let live = true;
    api<ChatMessage[]>('GET', `/chats/${id}/messages`)
      .then((m) => live && useNova.getState().set({ chatMessages: { ...useNova.getState().chatMessages, [id]: m } }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [id]);
  useEffect(() => {
    thread.current?.scrollTo({ top: thread.current.scrollHeight });
  }, [messages?.length, typing]);
  // Typing fades after a few seconds without news
  const [, tick] = useState(0);
  useEffect(() => {
    if (!typing) return;
    const t = setTimeout(() => tick((n) => n + 1), 5200);
    return () => clearTimeout(t);
  }, [typing]);

  const ch = CHANNELS.chat;
  const from = chat.pageUrl ? hostOf(chat.pageUrl) : null;
  return (
    <>
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-3">
        <span className="grid h-9 w-9 place-items-center rounded-lg text-white" style={{ background: ch.color }}>
          <Icon name={ch.icon} size={17} />
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold">{chat.customerName ?? 'Website visitor'}</p>
          <p className="truncate text-[12px] text-ink-3">
            Livechat{chat.queueName ? ` · ${chat.queueName}` : ''}
            {chat.customerEmail ? ` · ${chat.customerEmail}` : ''}
            {from ? ` · from ${from}` : ''}
          </p>
        </div>
      </div>

      {chat.status === 'ringing' ? (
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line bg-reserved/10 px-4 py-3">
          <p className="text-sm font-medium">New chat from {chat.customerName ?? 'a visitor'}</p>
          <span className="text-[12px] text-ink-3">
            waited <Elapsed since={chat.queuedAt ?? chat.startedAt} />
          </span>
          <span className="ml-auto flex gap-2">
            <Button tone="primary" className="px-4 py-2" autoFocus onClick={() => void act(() => api('POST', `/chats/${id}/accept`))}>
              Accept
            </Button>
            <Button tone="quiet" className="px-3 py-2" onClick={() => void act(() => api('POST', `/chats/${id}/decline`).then(() => true))}>
              Decline
            </Button>
          </span>
        </div>
      ) : chat.status === 'connected' ? (
        <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2" role="toolbar" aria-label="Chat controls">
          <span className="text-[12px] text-ink-3">
            Chatting for <Elapsed since={chat.answeredAt} />
          </span>
          <Button tone="danger" className="ml-auto px-3.5 py-1.5 text-sm" onClick={() => void act(() => api('POST', `/chats/${id}/end`).then(() => true))}>
            End chat
          </Button>
        </div>
      ) : null}

      <div ref={thread} className="min-h-0 flex-1 overflow-auto bg-abyss/40 p-4" aria-label="Conversation" aria-live="polite">
        {!messages ? (
          <p className="text-sm text-ink-3">Loading the conversation…</p>
        ) : (
          messages.map((m) => <Bubble key={m.id} m={m} />)
        )}
        {typing && chat.status === 'connected' ? <p className="mt-2 text-[12px] italic text-ink-3">{chat.customerName ?? 'The visitor'} is typing…</p> : null}
      </div>

      {chat.status === 'connected' ? <Composer id={id} customer={chat.customerName} /> : null}
      {chat.status === 'wrap_up' ? <ChatWrap id={id} name={chat.customerName} /> : null}
    </>
  );
}

export function Bubble({ m }: { m: ChatMessage }) {
  if (m.sender === 'system') return <p className="my-2 text-center text-[12px] text-ink-3">{m.body}</p>;
  const mine = m.sender === 'agent';
  return (
    <div className={`my-1.5 flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${mine ? 'rounded-br-md bg-ion text-on-accent' : 'rounded-bl-md border border-line bg-hull'}`}>
        {m.body}
        {m.files?.length ? <FileList files={m.files} light={mine} /> : null}
        <span className={`mt-0.5 block text-right text-[10px] ${mine ? 'opacity-75' : 'text-ink-3'}`}>
          {mine && m.agentName ? `${m.agentName.split(' ')[0]} · ` : ''}
          {new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

function Composer({ id, customer }: { id: string; customer: string | null }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const lastTyping = useRef(0);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const canned = useCanned('chat');
  // "/short" at the start: the canned responses whose shortcut starts with it
  const slash = /^\/([a-z0-9_-]*)$/i.exec(text.trim())?.[1]?.toLowerCase();
  const suggestions = slash !== undefined ? canned.filter((c) => c.shortcut && c.shortcut.startsWith(slash)).slice(0, 6) : [];

  async function send() {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    const ok = await act(() => api('POST', `/chats/${id}/messages`, { body }));
    setBusy(false);
    if (ok) setText('');
  }
  async function sendFile(file: File) {
    setBusy(true);
    await act(() => upload(`/chats/${id}/files`, file));
    setBusy(false);
  }
  function insert(t: string) {
    setText(t);
    setTimeout(() => box.current?.focus(), 0);
  }
  return (
    <div className="relative shrink-0 border-t border-line p-3">
      {suggestions.length ? (
        <ul className="absolute bottom-full left-3 z-40 mb-1 w-[min(360px,80%)] rounded-xl border border-line-strong bg-hull p-1 shadow-xl" aria-label="Canned responses">
          {suggestions.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => insert(fill(c.body, customer))} className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-veil/[0.05]">
                <span className="text-sm font-medium">/{c.shortcut}</span> <span className="text-[12px] text-ink-3">{c.title}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-2">
        <CannedPicker channel="chat" customer={customer} onPick={insert} />
        <button
          type="button"
          onClick={() => picker.current?.click()}
          disabled={busy}
          aria-label="Send a file"
          title="Send a file (images, PDF, Office documents; up to 10 MB)"
          className="grid h-9 w-9 place-items-center rounded-lg border border-line text-ink-2 hover:border-line-strong hover:text-ink"
        >
          <Icon name="clip" size={16} />
        </button>
        <input
          ref={picker}
          type="file"
          hidden
          aria-label="File to send"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void sendFile(f);
          }}
        />
        <textarea
          ref={box}
          value={text}
          rows={2}
          maxLength={4000}
          aria-label="Reply"
          placeholder="Type a reply. Enter sends, Shift+Enter for a new line, / for canned responses."
          onChange={(e) => {
            setText(e.target.value);
            if (Date.now() - lastTyping.current > 3000) {
              lastTyping.current = Date.now();
              void api('POST', `/chats/${id}/typing`).catch(() => undefined);
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey && suggestions.length) {
              e.preventDefault();
              insert(fill(suggestions[0].body, customer));
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          className="min-h-[44px] flex-1 resize-none rounded-lg border border-line bg-hull px-3 py-2 text-sm outline-none focus:border-ion"
        />
        <Button tone="primary" className="px-4 py-2.5" disabled={busy || !text.trim()} onClick={() => void send()}>
          Send
        </Button>
      </div>
    </div>
  );
}

/** After a chat or an email: choose an outcome, as after a call. */
export function ChatWrap({ id, name, what = 'chat' }: { id: string; name: string | null; what?: 'chat' | 'email' }) {
  const dispositions = useNova((s) => s.dispositions);
  const [chosen, setChosen] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!chosen) return;
    setBusy(true);
    const ok = await act(() => api('POST', `/interactions/${id}/disposition`, { dispositionId: chosen, notes }), what === 'chat' ? 'Chat wrapped up' : 'Email wrapped up');
    setBusy(false);
    if (ok) useNova.getState().set({ selectedId: null });
  }
  return (
    <div className="shrink-0 border-t border-line p-4" aria-label="Wrap up">
      <p className="mb-2 text-sm font-medium">Wrap up the {what} with {name ?? (what === 'chat' ? 'the visitor' : 'the customer')}</p>
      <div className="mb-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Outcome">
        {dispositions.map((d) => (
          <button
            key={d.id}
            type="button"
            role="radio"
            aria-checked={chosen === d.id}
            onClick={() => setChosen(d.id)}
            className={`rounded-lg border px-3 py-1.5 text-sm ${chosen === d.id ? 'border-ion bg-ion/10 text-ion' : 'border-line hover:border-line-strong'}`}
          >
            {d.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" aria-label="Notes" className="min-w-0 flex-1 rounded-lg border border-line bg-hull px-3 py-2 text-sm outline-none focus:border-ion" />
        <Button tone="primary" className="px-4 py-2" disabled={!chosen || busy} onClick={() => void save()}>
          Done
        </Button>
      </div>
    </div>
  );
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
